import React, { useState, useEffect } from "react";
import { FaRegUser, FaCheck } from "react-icons/fa";
import { format } from "date-fns";
import { bankingPartnerAPI } from "../../../services/AdminPortal/api";
import { toast } from "react-toastify";

const BankRepresentativeSection = ({ title, data }) => (
  <div className="w-full">
    <div className="font-medium text-customWhite mb-4">{title}</div>
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-2">
      <div>
        <div className="text-customWhite mb-1">Bank Representative</div>
        <div className="font-medium">{data?.name || "-"}</div>
      </div>
      <div>
        <div className="text-customWhite mb-1">Designation</div>
        <div className="font-medium">{data?.designation || "-"}</div>
      </div>
      <div>
        <div className="text-customWhite mb-1">Phone Number</div>
        <div className="font-medium">{data?.phone || "-"}</div>
      </div>
      <div>
        <div className="text-customWhite mb-1">Email ID</div>
        <div className="font-medium">{data?.email || "-"}</div>
      </div>
    </div>
  </div>
);

// Format date function
const formatDate = (dateString) => {
  if (!dateString) return "-";
  try {
    return format(new Date(dateString), "MMM dd, yyyy");
  } catch (error) {
    console.error("Error formatting date:", error);
    return dateString;
  }
};

export default function BankInfoTab({ partnerData, sendNotification }) {
  const [representatives, setRepresentatives] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Fetch representatives for this specific banking partner
  const fetchRepresentatives = async () => {
    if (!partnerData?._id) return;

    setLoading(true);
    setError(null);
    try {
      const response = await bankingPartnerAPI.getRepresentatives(
        partnerData._id
      );
      setRepresentatives(response.data || []);
    } catch (err) {
      console.error("Error fetching representatives:", err);
      setError(err.message || "Failed to fetch representatives");
      toast.error(err.message || "Failed to fetch representatives");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRepresentatives();
  }, [partnerData?._id]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-yellow-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-8 text-red-400">
        <p>Error loading representatives: {error}</p>
      </div>
    );
  }

  // Group representatives by designation
  const groupedRepresentatives = {
    manager: representatives.find(
      (rep) =>
        rep.designation?.toLowerCase().includes("manager") &&
        !rep.designation?.toLowerCase().includes("assistant")
    ),
    assistantManager: representatives.find((rep) =>
      rep.designation?.toLowerCase().includes("assistant manager")
    ),
    executive: representatives.find((rep) =>
      rep.designation?.toLowerCase().includes("executive")
    ),
    loanSalesManager: representatives.find(
      (rep) =>
        rep.designation?.toLowerCase().includes("loan sales manager") ||
        rep.designation?.toLowerCase().includes("sales manager")
    ),
  };

  return (
    <>
      {/* Bank Information */}
      <div className="mb-6">
        <div className="bg-[#131313] border border-customBorder p-4 rounded-md">
          <div className="flex items-center mb-4">
            <FaRegUser size={18} color="#FFCA20" />
            <h3 className="font-semibold ml-2">Bank Information</h3>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <div className="text-customWhite mb-1">IFSC Code</div>
              <div className="font-medium">
                {partnerData?.branchIFSCCode || "-"}
              </div>
            </div>
            <div>
              <div className="text-customWhite mb-1">Branch Name</div>
              <div className="font-medium">
                {partnerData?.branchName || "-"}
              </div>
            </div>
            <div>
              <div className="text-customWhite mb-1">Partnership Date</div>
              <div className="font-medium">
                {partnerData?.createdAt
                  ? formatDate(partnerData.createdAt)
                  : "-"}
              </div>
            </div>
            <div className="sm:col-span-2">
              <div className="text-customWhite mb-1">Bank Address</div>
              <div className="font-medium">
                {partnerData?.bankAddress || "-"}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bank Representatives */}
      <div className="bg-[#131313] border border-customBorder p-4 rounded-md mb-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center">
            <FaRegUser size={18} color="#FFCA20" />
            <h3 className="font-semibold ml-2">Bank Representatives</h3>
          </div>
          <span className="text-sm text-gray-400">
            {representatives.length} representative
            {representatives.length !== 1 ? "s" : ""} found
          </span>
        </div>

        {representatives.length === 0 ? (
          <div className="text-center py-8 text-gray-400">
            No representatives found for this banking partner.
          </div>
        ) : (
          <>
            <div className="flex flex-col lg:flex-row lg:gap-4 gap-6">
              <div className="flex-1">
                <BankRepresentativeSection
                  title="MANAGER"
                  data={
                    groupedRepresentatives.manager ||
                    partnerData?.representatives?.manager
                  }
                />
              </div>
              <div className="hidden lg:flex justify-center">
                <div className="w-px h-full bg-customBorder" />
              </div>
              <div className="flex-1">
                <BankRepresentativeSection
                  title="ASSISTANT MANAGER"
                  data={
                    groupedRepresentatives.assistantManager ||
                    partnerData?.representatives?.assistantManager
                  }
                />
              </div>
            </div>
            <div className="w-full h-px bg-customBorder my-6" />
            <div className="flex flex-col lg:flex-row lg:gap-4 gap-6">
              <div className="flex-1">
                <BankRepresentativeSection
                  title="EXECUTIVE"
                  data={
                    groupedRepresentatives.executive ||
                    partnerData?.representatives?.executive
                  }
                />
              </div>
              <div className="hidden lg:flex justify-center">
                <div className="w-px h-full bg-customBorder" />
              </div>
              <div className="flex-1">
                <BankRepresentativeSection
                  title="LOAN SALES MANAGER"
                  data={
                    groupedRepresentatives.loanSalesManager ||
                    partnerData?.representatives?.loanSalesManager
                  }
                />
              </div>
            </div>
          </>
        )}
      </div>
    </>
  );
}
